/** Dashboard do cliente: produção (auto) + crescimento (Search Console) + registro. Onda 18 v2. */
import { useEffect, useState } from "react";
import type { ChangeEvent, FormEvent } from "react";
import { Link, useParams } from "react-router";
import type { ClientGrowthDTO } from "@unik/shared";
import { api } from "../../lib/api";
import type { Metric } from "../../lib/metrics";
import { fmtInt, fmtGrowthPerMonth } from "../../lib/metrics";
import { AdminShell } from "../../ui/AdminShell";
import { Button } from "../../ui/Button";
import { Card } from "../../ui/Card";
import { Input } from "../../ui/Input";
import { GrowthPanel } from "../../ui/GrowthPanel";

interface Operational {
  draft: number; pending: number; revision: number; approved: number;
  total: number; aquecer: number; qualificar: number; vender: number;
}
interface Dash {
  operational: Operational;
  metrics: Metric[];
  growth: ClientGrowthDTO;
}

function thisMonday(): string {
  const d = new Date();
  const offset = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - offset);
  return d.toISOString().slice(0, 10);
}

type Form = Record<string, string>;
const FIELDS: { key: string; label: string }[] = [
  { key: "revenue", label: "Faturamento (R$)" },
  { key: "avgTicket", label: "Ticket médio (R$)" },
  { key: "appointments", label: "Agendamentos" },
  { key: "followers", label: "Seguidores" },
  { key: "reach", label: "Alcance" },
  { key: "impressions", label: "Impressões" },
  { key: "profileVisits", label: "Visitas ao perfil" },
  { key: "engagement", label: "Interações" },
  { key: "storyViews", label: "Visualizações de stories" },
  { key: "storyViewsNextDays", label: "Expectativa p/ próximos dias (stories)" },
];
function emptyForm(): Form {
  const f: Form = { period: thisMonday() };
  for (const x of FIELDS) f[x.key] = "";
  return f;
}

export function Dashboard() {
  const { id } = useParams();
  const [dash, setDash] = useState<Dash | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<Form>(emptyForm());
  const [baseline, setBaseline] = useState(false);
  const [saving, setSaving] = useState(false);
  const [report, setReport] = useState<{ url: string; whatsappUrl: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    api.get<Dash>(`/clients/${id}/dashboard`).then(setDash).catch(() => setError("Não foi possível carregar."));
  }, [id]);

  const set = (k: string) => (e: ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function salvar(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      const metrics = await api.post<Metric[]>(`/clients/${id}/metrics`, { ...form, isBaseline: baseline });
      setDash((d) => (d === null ? d : { ...d, metrics }));
      setForm(emptyForm());
      setBaseline(false);
    } finally {
      setSaving(false);
    }
  }

  async function compartilhar() {
    setSharing(true);
    try {
      const res = await api.post<{ url: string; whatsappUrl: string }>(`/clients/${id}/report/share`, {});
      setReport(res);
      setCopied(false);
      await navigator.clipboard.writeText(res.url).catch(() => undefined);
      setCopied(true);
    } finally {
      setSharing(false);
    }
  }
  async function revogarRelatorio() {
    await api.post(`/clients/${id}/report/revoke`).catch(() => undefined);
    setReport(null);
  }

  if (error !== null) return <AdminShell><p className="type-body text-danger">{error}</p></AdminShell>;
  if (dash === null) return <AdminShell><p className="type-body text-text-secondary">Carregando…</p></AdminShell>;

  const op = dash.operational;
  const purposeTotal = op.aquecer + op.qualificar + op.vender || 1;

  return (
    <AdminShell>
      <div className="flex items-center justify-between gap-3">
        <div>
          <Link to={`/clientes/${id}`} className="type-caption text-text-tertiary">← Voltar ao cliente</Link>
          <h1 className="type-h1 mt-1 text-text-cream">Dashboard</h1>
        </div>
        <Button size="sm" variant="secondary" onClick={compartilhar} loading={sharing}>
          Link de visualização (sem login)
        </Button>
      </div>
      {report !== null && (
        <div className="mt-3 flex flex-wrap items-center gap-3 rounded-[var(--radius-control)] border border-border-subtle p-3">
          <p className="type-caption break-all text-gold">{copied ? "Copiado! " : ""}{report.url}</p>
          <a href={report.whatsappUrl} target="_blank" rel="noreferrer">
            <Button size="sm">Abrir no WhatsApp</Button>
          </a>
          <Button size="sm" variant="ghost" onClick={revogarRelatorio}>Desativar link</Button>
        </div>
      )}

      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[["Aprovados", op.approved], ["Em aprovação", op.pending], ["Rascunhos", op.draft], ["Em revisão", op.revision]].map(
          ([label, value]) => (
            <Card key={label as string} className="text-center">
              <p className="type-h1 text-text-cream">{value}</p>
              <p className="type-caption mt-1 text-text-tertiary">{label}</p>
            </Card>
          ),
        )}
      </div>

      <Card className="mt-4">
        <h2 className="type-h3 text-text-cream">Distribuição por função</h2>
        <div className="mt-3 flex h-3 overflow-hidden rounded-full">
          <span className="bg-text-secondary" style={{ width: `${(op.aquecer / purposeTotal) * 100}%` }} />
          <span className="bg-gold" style={{ width: `${(op.qualificar / purposeTotal) * 100}%` }} />
          <span className="bg-surface-brand" style={{ width: `${(op.vender / purposeTotal) * 100}%` }} />
        </div>
        <div className="mt-2 flex gap-4 type-caption text-text-tertiary">
          <span>aquecer {op.aquecer}</span>
          <span>qualificar {op.qualificar}</span>
          <span>vender {op.vender}</span>
        </div>
      </Card>

      <h2 className="type-h3 mt-8 text-text-cream">Resumo de crescimento</h2>
      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Card className="text-center">
          <p className="type-caption text-text-tertiary">Seguidores iniciais</p>
          <p className="type-h2 mt-1 text-text-cream">
            {dash.growth.followersBaseline === null ? "—" : fmtInt(dash.growth.followersBaseline)}
          </p>
        </Card>
        <Card className="text-center">
          <p className="type-caption text-text-tertiary">Crescimento médio</p>
          <p className="type-h2 mt-1 text-text-cream">{fmtGrowthPerMonth(dash.growth.followersGrowthPerMonth)}</p>
        </Card>
        <Card className="text-center">
          <p className="type-caption text-text-tertiary">Seguidores agora</p>
          <p className="type-h2 mt-1 text-text-cream">
            {dash.growth.followersCurrent === null ? "—" : fmtInt(dash.growth.followersCurrent)}
          </p>
        </Card>
        <Card className="text-center">
          <p className="type-caption text-text-tertiary">Stories · última medição</p>
          <p className="type-h2 mt-1 text-text-cream">
            {dash.growth.storyViewsCurrent === null ? "—" : fmtInt(dash.growth.storyViewsCurrent)}
          </p>
        </Card>
        <Card className="text-center">
          <p className="type-caption text-text-tertiary">Stories · média</p>
          <p className="type-h2 mt-1 text-text-cream">
            {dash.growth.storyViewsAverage === null ? "—" : fmtInt(dash.growth.storyViewsAverage)}
          </p>
        </Card>
        <Card className="text-center">
          <p className="type-caption text-text-tertiary">Stories · expectativa próx. dias</p>
          <p className="type-h2 mt-1 text-text-cream">
            {dash.growth.storyViewsNextDays === null ? "—" : fmtInt(dash.growth.storyViewsNextDays)}
          </p>
        </Card>
      </div>

      <h2 className="type-h3 mt-8 text-text-cream">Gráfico</h2>
      <p className="type-body-sm mt-1 text-text-secondary">Clique nos cards para mostrar/ocultar as linhas do gráfico.</p>
      <div className="mt-3">
        <GrowthPanel metrics={dash.metrics} />
      </div>

      <Card className="mt-6">
        <h2 className="type-h3 text-text-cream">Registrar números</h2>
        <p className="type-body-sm mt-1 text-text-secondary">
          No cadastro, marque "marco inicial". Depois, registre com a data de cada medição.
        </p>
        <form onSubmit={salvar} className="mt-4 flex flex-col gap-4">
          <Input label="Data da medição" type="date" value={form.period ?? ""} onChange={set("period")} />
          <div className="grid grid-cols-2 gap-3">
            {FIELDS.map((x) => (
              <Input key={x.key} label={x.label} inputMode="numeric" value={form[x.key] ?? ""} onChange={set(x.key)} />
            ))}
          </div>
          <label className="flex items-center gap-2 type-body-sm text-text-secondary">
            <input type="checkbox" checked={baseline} onChange={(e) => setBaseline(e.target.checked)} />
            É o marco inicial (o "antes", no cadastro)
          </label>
          <div>
            <Button type="submit" loading={saving}>Salvar números</Button>
          </div>
        </form>
      </Card>
    </AdminShell>
  );
}
